import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../../../../environments/environment";
import {
  VigilantApiDtosOrgsGetResponse,
  VigilantApiDtosOrgsPostRequest,
  VigilantApiDtosOrgsPutRequest,
  VigilantApiDtosOrgsUsersGetResponse,
} from "./orgs.model";

@Injectable({ providedIn: "root" })
export class VigilantOrgsService {
  constructor(private http: HttpClient) {}

  url = environment.vigilantApiUrl;

  /**
   * Retrieves all active Channel.
   *
   * @returns {Observable<VigilantApiDtosOrgsGetResponse[]>} An Observable emitting an array of active Channel objects.
   */
  getAllActiveOrgs(): Observable<VigilantApiDtosOrgsGetResponse[]> {
    return this.http.get<VigilantApiDtosOrgsGetResponse[]>(`${this.url}/api/Orgs/active`);
  }

  /**
   * Retrieves an Channel by its unique identifier.
   *
   * @param {number} id - The unique identifier of the Channel.
   * @returns {Observable<VigilantApiDtosOrgsGetResponse>} An Observable emitting the Channel object corresponding to the provided id.
   */
  getOrgById(id: number): Observable<VigilantApiDtosOrgsGetResponse> {
    return this.http.get<VigilantApiDtosOrgsGetResponse>(`${this.url}/api/Orgs/${id}`);
  }

  /**
   * Creates a new Channel.
   *
   * @param {VigilantApiDtosOrgsPostRequest} org - The Channel data used to create the new Channel.
   * @returns {Observable<VigilantApiDtosOrgsGetResponse>} An Observable emitting the created Channel object.
   */
  createOrg(org: VigilantApiDtosOrgsPostRequest): Observable<VigilantApiDtosOrgsGetResponse> {
    return this.http.post<VigilantApiDtosOrgsGetResponse>(`${this.url}/api/Orgs`, org);
  }

  /**
   * Updates an existing Channel.
   *
   * @param {number} id - The unique identifier of the Channel to update.
   * @param {VigilantApiDtosOrgsPutRequest} org - The updated Channel data.
   * @returns {Observable<VigilantApiDtosOrgsGetResponse>} An Observable emitting the updated Channel object.
   */
  updateOrg(id: number, org: VigilantApiDtosOrgsPutRequest): Observable<VigilantApiDtosOrgsGetResponse> {
    return this.http.put<VigilantApiDtosOrgsGetResponse>(`${this.url}/api/Orgs/${id}`, org);
  }

  /**
   * Deletes an Channel by its unique identifier.
   *
   * @param {number} id - The unique identifier of the Channel to delete.
   * @returns {Observable<unknown>} An Observable emitting the result of the delete operation.
   */
  deleteOrg(id: number): Observable<unknown> {
    return this.http.delete<unknown>(`${this.url}/api/Orgs/${id}`);
  }

  /**
   * Adds a user to an Channel's Matrix room.
   *
   * @param {number} userId - The unique identifier of the user.
   * @param {number} orgId - The unique identifier of the Channel.
   * @returns {Observable<VigilantApiDtosOrgsUsersGetResponse>} An Observable emitting the updated Channel user details after joining the Matrix room.
   */
  addUserToOrgMatrixRoom(userId: number, orgId: number): Observable<VigilantApiDtosOrgsUsersGetResponse> {
    return this.http.patch<VigilantApiDtosOrgsUsersGetResponse>(`${this.url}/api/Orgs/${orgId}/users/${userId}/join`, {});
  }

  /**
   * Removes a user from an Channel's Matrix room.
   *
   * @param {number} userId - The unique identifier of the user.
   * @param {number} orgId - The unique identifier of the Channel.
   * @returns {Observable<VigilantApiDtosOrgsUsersGetResponse>} An Observable emitting the updated Channel user details after leaving the Matrix room.
   */
  removeUserFromOrgMatrixRoom(userId: number, orgId: number): Observable<VigilantApiDtosOrgsUsersGetResponse> {
    return this.http.patch<VigilantApiDtosOrgsUsersGetResponse>(`${this.url}/api/Orgs/${orgId}/users/${userId}/leave`, {});
  }
}
